import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { logException } from 'App/logging';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import { useUserEntitlements } from 'hooks/useUserEntitlements';
import {
  getPendingMessagePendingMessageGetResponseError,
  getPendingMessagePendingMessageGet as getPendingMessage,
} from 'Assistant/__generated__/client';
import { useAuthHeader } from 'Assistant/Chatbot/hooks/useAuthHeader';

/**
 * Responses that mean the app is working correctly rather than that something
 * needs fixing: the user's session or entitlement isn't valid. We also filter
 * out service unavailable status because that is monitored elsewhere and it's
 * not helpful to have it reported separately from each network request.
 */
const EXPECTED_STATUSES = [401, 403, 503];

/**
 * A 5xx is already reported server-side, with a stack trace we don't have.
 * Reporting it again here would be noise, so we only surface responses that
 * point at a bug on this side of the wire.
 */
const isReportableStatus = (
  status: getPendingMessagePendingMessageGetResponseError['status'],
) => !EXPECTED_STATUSES.includes(status);

export type PendingMessageContextValue = {
  hasPendingMessage: boolean;
  clearPendingMessage(): void;
};

/**
 * Defaults to no pending message so components which read the badge state can
 * be rendered (and tested) without the provider.
 */
export const PendingMessageContext = createContext<PendingMessageContextValue>({
  hasPendingMessage: false,
  clearPendingMessage: () => undefined,
});

export const usePendingMessage = () => useContext(PendingMessageContext);

interface PendingMessageProviderProps {
  children: ReactNode;
}

/**
 * Queries the Chat API to find out whether nova has a message pending for the
 * user. It is mounted inside the authenticated tree (see `PageWithNavigation`),
 * so it queries once per sign-in: logging out unmounts it, and logging back in
 * mounts a fresh instance with the new user's auth header. There is
 * deliberately no explicit refresh within a session: a newly pending message
 * surfaces on the next sign-in or full page reload. We could poll the API, but
 * page reloads and re-authentication tend to happen roughly daily, and precise
 * timing of the message being pending is not important.
 */
const PendingMessageProvider = ({ children }: PendingMessageProviderProps) => {
  const [hasPendingMessage, setHasPendingMessage] = useState(false);
  const isBadgeEnabled = useFeatureFlag(
    FEATURE_FLAGS.NOVA_WEB_PENDING_MESSAGE_BADGE,
  );
  const { entitlements } = useUserEntitlements();
  const accessTokenHeader = useAuthHeader();

  const clearPendingMessage = useCallback(() => {
    setHasPendingMessage(false);
  }, []);

  useEffect(() => {
    if (!isBadgeEnabled || !entitlements.nova || !accessTokenHeader) {
      // Drop any badge we are already showing, so turning the flag off (or
      // losing the entitlement) takes effect in live sessions rather than on
      // the next page load.
      clearPendingMessage();

      return;
    }

    let isCurrent = true;

    const fetchPendingMessage = async () => {
      try {
        const { data, status } = await getPendingMessage({
          headers: {
            'client-platform': 'web',
            ...accessTokenHeader,
          },
        });

        if (status !== 200) {
          if (isReportableStatus(status)) {
            // The status goes in the context rather than the message, so every
            // occurrence groups into one Sentry issue.
            logException(
              new Error('Unexpected response fetching nova pending message'),
              {
                tags: {
                  product: 'nova',
                  feature: 'nova-pending-message-badge',
                  action: 'fetch-pending-message',
                },
                extra: { status },
              },
            );
          }

          return;
        }

        if (isCurrent) {
          setHasPendingMessage(data.pending_message);
        }
      } catch (ex) {
        logException(ex, {
          tags: {
            product: 'nova',
            feature: 'nova-pending-message-badge',
            action: 'fetch-pending-message',
          },
        });
      }
    };

    void fetchPendingMessage();

    return () => {
      isCurrent = false;
    };
  }, [
    isBadgeEnabled,
    entitlements.nova,
    accessTokenHeader,
    clearPendingMessage,
  ]);

  const value = useMemo(
    () => ({ hasPendingMessage, clearPendingMessage }),
    [hasPendingMessage, clearPendingMessage],
  );

  return (
    <PendingMessageContext.Provider value={value}>
      {children}
    </PendingMessageContext.Provider>
  );
};

export default PendingMessageProvider;
